import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { QrCode, Eye, Download, Copy } from 'lucide-react';
import QRCodeStyling from 'qr-code-styling';
import { Badge } from '../Badge';
import { QRCodeResponse } from '../../types/qr.types';
import { copyToClipboard } from '../../utils/clipboard';
import { getQrTargetUrl } from '../../utils/qr';
import { SAMPLE_QR_SHORT_CODE } from '../../data/sampleQrCode';
import toast from 'react-hot-toast';

interface QRCodeTableProps {
  qrCodes: QRCodeResponse[];
  scanCounts?: Record<string, number>;
  isLoadingScans?: boolean;
  compact?: boolean;
  selection?: {
    selectedIds: ReadonlySet<number>;
    onToggle: (id: number) => void;
    onToggleAll: () => void;
    disabled?: boolean;
  };
}

export function QRCodeTable({
  qrCodes,
  scanCounts = {},
  isLoadingScans = false,
  compact = false,
  selection,
}: QRCodeTableProps) {
  const navigate = useNavigate();
  const selectAllRef = useRef<HTMLInputElement>(null);
  const selectableCodes = qrCodes.filter(code => code.id > 0 && code.short_code !== SAMPLE_QR_SHORT_CODE);
  const selectedCount = selectableCodes.filter(code => selection?.selectedIds.has(code.id)).length;
  const allSelected = selectableCodes.length > 0 && selectedCount === selectableCodes.length;
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = selectedCount > 0 && !allSelected;
  }, [selectedCount, allSelected]);

  const handleDownloadQR = async (qrCode: QRCodeResponse, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const qrStyling = new QRCodeStyling({
        type: 'svg',
        width: 1000,
        height: 1000,
        data: getQrTargetUrl(qrCode),
        margin: 40,
        image: qrCode.custom_logo_url || undefined,
        qrOptions: { errorCorrectionLevel: qrCode.custom_logo_url ? 'H' : 'M' },
        imageOptions: {
          crossOrigin: 'anonymous',
          margin: 20,
          imageSize: 0.4,
          hideBackgroundDots: true,
        },
        dotsOptions: { type: qrCode.qr_style?.dots_type || 'square', color: qrCode.foreground_color || '#000000' },
        cornersSquareOptions: { type: qrCode.qr_style?.corners_square_type || 'square', color: qrCode.foreground_color || '#000000' },
        cornersDotOptions: { type: qrCode.qr_style?.corners_dot_type || 'square', color: qrCode.foreground_color || '#000000' },
        backgroundOptions: { color: qrCode.background_color || '#FFFFFF' },
      });
      await qrStyling.download({ name: `${qrCode.title}-qr`, extension: 'png' });
      toast.success('QR code downloaded!');
    } catch (error) {
      toast.error('Failed to download QR code');
    }
  };

  const handleCopyUrl = async (qrCode: QRCodeResponse, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      // Construct URL directly to avoid async API call (Safari requires synchronous clipboard access)
      const url = getQrTargetUrl(qrCode);
      await copyToClipboard(url);
      toast.success('URL copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy URL');
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="bg-white rounded-xl border border-[--clinqr-border]/10 overflow-x-auto shadow-sm">
      <table className="w-full">
        <thead>
          <tr className="border-b border-[--clinqr-border]/10 bg-[--clinqr-bg-subtle]/50">
            {selection && (
              <th className="pl-6 py-4 w-12">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  aria-label="Select all QR codes on this page"
                  checked={allSelected}
                  onChange={selection.onToggleAll}
                  disabled={selection.disabled || selectableCodes.length === 0}
                  className="h-4 w-4 accent-[--clinqr-primary]"
                />
              </th>
            )}
            <th className="px-6 py-4 text-left text-xs font-semibold text-[--clinqr-text-muted] uppercase tracking-wider">
              QR Code
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-[--clinqr-text-muted] uppercase tracking-wider">
              Type
            </th>
            <th className="px-6 py-4 text-left text-xs font-semibold text-[--clinqr-text-muted] uppercase tracking-wider">
              Status
            </th>
            {!compact && (
              <th className="px-6 py-4 text-left text-xs font-semibold text-[--clinqr-text-muted] uppercase tracking-wider">
                Scans
              </th>
            )}
            <th className="px-6 py-4 text-left text-xs font-semibold text-[--clinqr-text-muted] uppercase tracking-wider">
              Created
            </th>
            {!selection && <th className="px-6 py-4 text-right text-xs font-semibold text-[--clinqr-text-muted] uppercase tracking-wider">
              Actions
            </th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-[--clinqr-border]/5">
          {qrCodes.map((qrCode) => (
            <tr
              key={qrCode.id}
              aria-selected={selection ? selection.selectedIds.has(qrCode.id) : undefined}
              className="hover:bg-gray-100 transition-colors cursor-pointer group"
              onClick={() => {
                if (selection) {
                  if (!selection.disabled && qrCode.id > 0 && qrCode.short_code !== SAMPLE_QR_SHORT_CODE) {
                    selection.onToggle(qrCode.id);
                  }
                } else navigate(`/dashboard/qr-codes/${qrCode.short_code}`);
              }}
            >
              {selection && (
                <td className="pl-6 py-4" onClick={event => event.stopPropagation()}>
                  {qrCode.id > 0 && qrCode.short_code !== SAMPLE_QR_SHORT_CODE && (
                    <input
                      type="checkbox"
                      aria-label={`Select ${qrCode.title}`}
                      checked={selection.selectedIds.has(qrCode.id)}
                      onChange={() => selection.onToggle(qrCode.id)}
                      disabled={selection.disabled}
                      className="h-4 w-4 accent-[--clinqr-primary]"
                    />
                  )}
                </td>
              )}
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[--clinqr-bg-subtle] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[--clinqr-primary] transition-colors">
                    <QrCode className="w-5 h-5 text-[--clinqr-text-muted] group-hover:text-[--clinqr-bg] transition-colors" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-[--clinqr-text-primary] truncate">{qrCode.title}</p>
                      {qrCode.short_code === SAMPLE_QR_SHORT_CODE && (
                        <Badge variant="info" size="sm">Demo</Badge>
                      )}
                    </div>
                    {qrCode.fallback_image_id && qrCode.fallback_image_url ? (
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <img
                          src={qrCode.fallback_image_url}
                          alt="Destination"
                          className="w-4 h-4 object-contain rounded border border-[--clinqr-border]/20 bg-white flex-shrink-0"
                        />
                        <p className="text-xs text-[--clinqr-text-muted] truncate">Image</p>
                      </div>
                    ) : (
                      <p className="text-xs text-[--clinqr-text-muted] truncate" title={qrCode.fallback_url}>
                        {qrCode.fallback_url}
                      </p>
                    )}
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center gap-2">
                  {qrCode.has_rules ? (
                    <Badge variant="rules">
                      {qrCode.rules_count} RULE{qrCode.rules_count !== 1 ? 'S' : ''}
                    </Badge>
                  ) : (
                    <Badge variant="default">Standard</Badge>
                  )}
                  {qrCode.password_protected && (
                    <Badge variant="protected">PROTECTED</Badge>
                  )}
                </div>
              </td>
              <td className="px-6 py-4">
                <Badge variant={qrCode.is_active ? 'active' : 'inactive'}>
                  {qrCode.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </td>
              {!compact && (
                <td className="px-6 py-4">
                  <span className="text-[--clinqr-text-primary] font-bold">
                    {isLoadingScans ? '...' : (scanCounts[qrCode.short_code] || 0).toLocaleString()}
                  </span>
                </td>
              )}
              <td className="px-6 py-4 text-sm text-[--clinqr-text-secondary] font-medium">
                {compact ? formatTimeAgo(qrCode.created_at) : new Date(qrCode.created_at).toLocaleDateString()}
              </td>
              {!selection && <td className="px-6 py-4">
                <div className="flex items-center justify-end gap-2 transition-opacity">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/dashboard/qr-codes/${qrCode.short_code}`);
                    }}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[--clinqr-primary] hover:text-[--clinqr-bg] text-[--clinqr-text-secondary] transition-all"
                    title="View Details"
                  >
                    <Eye className="w-4 h-4" strokeWidth={2} />
                  </button>
                  <button
                    onClick={(e) => handleDownloadQR(qrCode, e)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[--clinqr-primary] hover:text-[--clinqr-bg] text-[--clinqr-text-secondary] transition-all"
                    title="Download QR"
                  >
                    <Download className="w-4 h-4" strokeWidth={2} />
                  </button>
                  <button
                    onClick={(e) => handleCopyUrl(qrCode, e)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[--clinqr-primary] hover:text-[--clinqr-bg] text-[--clinqr-text-secondary] transition-all"
                    title="Copy URL"
                  >
                    <Copy className="w-4 h-4" strokeWidth={2} />
                  </button>
                </div>
              </td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
